import React from 'react'
import { Dialog, BasicDialogBody } from '../../legacy-design-system/modal'
import { FormattedMessage } from 'react-intl'
import gql from 'graphql-tag'
import { Mutation, FetchResult } from 'react-apollo'
import { RenderDialogProps } from '../../legacy-design-system/action/confirmation-action'
import { PureQueryOptions } from 'apollo-client'
import { logError, logSuccess } from '../toaster'
import { ContrastFormattedMessage } from '../contrast-formatted-message'
import { useQueryClient } from 'react-query'
import { invalidateVrLicenseQueries } from '../value-reporting/modules/licenses/api/queries/vr-licenses'

interface ApplyApplicationLicenseResult {
  readonly applyApplicationLicense: {
    readonly id: string
    readonly __typename: string
  }
  readonly __typename: string
}

interface ApplyAppLicenseModalProps extends RenderDialogProps {
  readonly apply: () => Promise<FetchResult<ApplyApplicationLicenseResult> | void>
  readonly name: string
}

export function ApplyAppLicenseModal({
  isOpen,
  name,
  closeDialog,
  apply
}: ApplyAppLicenseModalProps) {
  const queryClient = useQueryClient()

  return (
    <Dialog
      isOpen={isOpen}
      onCancel={closeDialog}
      renderTitle={() => (
        <FormattedMessage
          id="action_applyLicense"
          defaultMessage="Apply License"
        />
      )}
      size="small"
    >
      <BasicDialogBody
        body={
          <FormattedMessage
            id="variable_applicationLicense_applyLicenseConfirmation_v2"
            values={{ applicationName: <strong>{name}</strong> }}
          />
        }
        confirmButtonText={<FormattedMessage id="action_applyLicense" />}
        cancelButtonText={<FormattedMessage id="action_close" />}
        onCancel={closeDialog}
        onConfirm={() => {
          closeDialog()
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          apply()
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          invalidateVrLicenseQueries(queryClient)
        }}
      />
    </Dialog>
  )
}

const applyApplicationLicenseMutation = gql`
  mutation ($organizationID: ID!, $applicationID: ID!) {
    applyApplicationLicense(
      input: { organizationID: $organizationID, id: $applicationID }
    ) {
      id
    }
  }
`

class LicenseMutation extends Mutation<
  ApplyApplicationLicenseResult,
  {
    readonly applicationID: string
    readonly organizationID: string
  }
> {}

export function ApplyApplicationLicenseDialog({
  application,
  organizationID,
  refetchQueries,
  refresh,
  ...props
}: RenderDialogProps & {
  readonly application: {
    readonly id: string
    readonly name: string
  }
  readonly organizationID: string
  readonly refetchQueries?: PureQueryOptions[]
  readonly refresh?: () => Promise<any>
}) {
  const { id, name } = application
  return (
    <LicenseMutation mutation={applyApplicationLicenseMutation}>
      {(applyApplicationLicense) => {
        return (
          <ContrastFormattedMessage
            id="variable_applicationLicense_licenseSuccessfullyApplied"
            values={{ applicationName: name }}
            children={(successMessage) => (
              <ContrastFormattedMessage
                id="variable_applicationLicense_errorApplyingLicense"
                values={{ applicationName: name }}
                children={(errorMessage) => (
                  <ApplyAppLicenseModal
                    {...props}
                    name={name}
                    apply={() => {
                      const applyLicense = applyApplicationLicense({
                        variables: { applicationID: id, organizationID },
                        refetchQueries,
                        optimisticResponse: {
                          applyApplicationLicense: {
                            id,
                            __typename: 'Application'
                          },
                          __typename: 'Mutation'
                        }
                      })

                      return !!refresh
                        ? applyLicense
                            .then(() => {
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises
                              refresh()
                              logSuccess(successMessage)
                            })
                            .catch((error) => {
                              logError(errorMessage)
                              return error
                            })
                        : applyLicense
                    }}
                  />
                )}
              />
            )}
          />
        )
      }}
    </LicenseMutation>
  )
}
